import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";

// "closed" and "full" are computed server-side (effective_status/is_full),
// never stored -- an event can be simultaneously Open and Full (dinner-only
// responses still allowed), so Full is its own badge, not a status.
const STATUS_STYLE = {
  draft:     { bg: "#f3f4f6", color: "#4b5563", label: "Draft" },
  open:      { bg: "#dcfce7", color: "#166534", label: "Open" },
  closed:    { bg: "#fef3c7", color: "#92400e", label: "Closed" },
  current:   { bg: "#dbeafe", color: "#1e40af", label: "Current Competition" },
  completed: { bg: "#ede9fe", color: "#5b21b6", label: "Completed" },
};

const DEFAULT_INVITE_SUBJECT = "⛳ {{event_name}} — are you coming?";
const DEFAULT_INVITE_BODY =
`Hi {{first_name}},

Our next fixture is {{event_name}} on {{date}} at {{venue}}.

Cost: {{golf_cost}}  Buggy: {{buggy_cost}}
Please let us know by {{closing_date}}.

Sign up: {{link_signup}}
Sign up with a buggy: {{link_buggy}}
Can't make it: {{link_decline}}

Thanks!`;
const MERGE_TAGS = ["first_name","name","event_name","date","venue","golf_cost","dinner_cost","buggy_cost","closing_date","link_signup","link_buggy","link_decline"];

const TABS = ["Players", "Reserves", "Costs", "Tee Times", "Communications"];

// Same list Planner.jsx's create form uses -- kept identical so a fixture's
// format never looks different between creating it and editing it later.
const GAME_TYPES = ['Singles Stableford','Singles Stableford (Full Handicap)','Singles Medal','Singles Stroke Play','Singles Match Play','Skins','Better Ball Stableford','Better Ball Medal','Better Ball Stroke Play','Better Ball Match Play','Fourball Better Ball Stableford','Team Stableford (Best 2 of 4)','Fourball Match Play','Fourball Stroke Play','Foursomes Stableford','Foursomes Medal','Foursomes Match Play','Greensomes Stableford','Greensomes Medal','Greensomes Match Play','Team Stableford','Team Medal','Team Aggregate Stableford','Team Aggregate Stroke Play','Team Match Play','Texas Scramble (4 players)','Texas Scramble (3 players)','Texas Scramble Medal (4 players)','Texas Scramble Medal (3 players)'];

export default function PlannerEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Players");
  const [toast, setToast] = useState("");
  const [confirmMakeCurrent, setConfirmMakeCurrent] = useState(false);
  const [makingCurrent, setMakingCurrent] = useState(false);

  const [inviteFilter, setInviteFilter] = useState("all");
  const [inviteSubject, setInviteSubject] = useState(DEFAULT_INVITE_SUBJECT);
  const [inviteBody, setInviteBody] = useState(DEFAULT_INVITE_BODY);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [allPlayers, setAllPlayers] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [addPlayerId, setAddPlayerId] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [confirmDeleteFixture, setConfirmDeleteFixture] = useState(false);
  const [deletingFixture, setDeletingFixture] = useState(false);
  const [updatingPlayerId, setUpdatingPlayerId] = useState(null);
  const [removingAll, setRemovingAll] = useState(false);

  const [courses, setCourses] = useState([]);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Bulk sign-up via spreadsheet -- same AI-powered /players/excel-parse
  // endpoint Players.jsx uses for bulk-adding new roster players, reused
  // here for a different job: matching names against the EXISTING roster
  // and recording /respond sign-ups for them, since Planner never creates
  // new players itself.
  const excelFileInputRef = useRef(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [excelError, setExcelError] = useState("");
  const [excelReview, setExcelReview] = useState(null);
  const [confirmingExcel, setConfirmingExcel] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/planner/${id}`);
      setData(res.data);
    } catch(e) { console.error(e); showToast("Couldn't load this fixture"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/players").then(r => setAllPlayers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/courses").then(r => setCourses(r.data)).catch(() => {}); }, []);

  if (loading) return <Layout><div style={{padding:"20px",color:"#6b7280",fontSize:"13px"}}>Loading…</div></Layout>;
  if (!data) return <Layout><div style={{padding:"20px",color:"#991b1b",fontSize:"13px"}}>Fixture not found.</div></Layout>;

  const { fixture, confirmed, reserve, declined } = data;
  const st = STATUS_STYLE[fixture.effective_status] || STATUS_STYLE.draft;
  const placesLeft = fixture.max_players != null ? Math.max(0, fixture.max_players - confirmed.filter(p => p.golf).length) : null;

  const publish = async () => {
    try { await api.post(`/planner/${id}/publish`); showToast("Published — members can now sign up"); load(); }
    catch(e) { showToast(e.response?.data?.error || "Couldn't publish"); }
  };

  // Reuses the already-existing (but previously never-called) PUT /planner/:id
  // route -- the backend already blocks this once status is current/completed,
  // so the button itself is hidden in that state rather than duplicating the check.
  const openEdit = () => {
    setEditForm({
      event_name: fixture.event_name || "",
      event_date: fixture.event_date ? fixture.event_date.slice(0, 10) : "",
      venue: fixture.venue || "",
      course_id: fixture.course_id || "",
      game_type: fixture.game_type || "Stableford",
      hcp_cap: fixture.hcp_cap != null ? String(fixture.hcp_cap) : "",
      first_tee_time: fixture.first_tee_time || "09:00",
      tee_interval_minutes: fixture.tee_interval_minutes || 8,
      max_players: fixture.max_players != null ? String(fixture.max_players) : "",
      closing_date: fixture.closing_date ? fixture.closing_date.slice(0, 10) : "",
      golf_cost: fixture.golf_cost != null ? String(fixture.golf_cost) : "",
      dinner_cost: fixture.dinner_cost != null ? String(fixture.dinner_cost) : "",
      buggy_cost: fixture.buggy_cost != null ? String(fixture.buggy_cost) : "",
      other_cost: fixture.other_cost != null ? String(fixture.other_cost) : "",
    });
    setShowEditForm(true);
  };

  const saveEdit = async () => {
    if (!editForm.event_name.trim() || !editForm.event_date) { showToast("Event name and date are required"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        course_id: editForm.course_id || null,
        hcp_cap: editForm.hcp_cap === "" ? null : parseInt(editForm.hcp_cap, 10),
        max_players: editForm.max_players === "" ? null : parseInt(editForm.max_players, 10),
        tee_interval_minutes: parseInt(editForm.tee_interval_minutes, 10) || 8,
        closing_date: editForm.closing_date || null,
        golf_cost: editForm.golf_cost === "" ? null : editForm.golf_cost,
        dinner_cost: editForm.dinner_cost === "" ? null : editForm.dinner_cost,
        buggy_cost: editForm.buggy_cost === "" ? null : editForm.buggy_cost,
        other_cost: editForm.other_cost === "" ? null : editForm.other_cost,
      };
      await api.put(`/planner/${id}`, payload);
      setShowEditForm(false);
      showToast("Fixture updated");
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't save changes"); }
    finally { setSavingEdit(false); }
  };

  const promote = async (attendanceId) => {
    try { await api.post(`/planner/${id}/promote/${attendanceId}`); showToast("Promoted to confirmed"); load(); }
    catch(e) { showToast(e.response?.data?.error || "Couldn't promote"); }
  };

  // Manually register a response on a player's behalf -- for members who
  // phone/text the organiser instead of using the public page, Player
  // Portal, or an email link. Same /respond endpoint those use, just
  // organiser-authed so it works even after the closing date.
  const addPlayerManually = async (choice, buggy) => {
    if (!addPlayerId) return;
    setAddingPlayer(true);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: parseInt(addPlayerId, 10), choice, buggy: !!buggy });
      showToast("Response recorded");
      setShowAddPlayer(false);
      setAddPlayerId("");
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't record that response"); }
    finally { setAddingPlayer(false); }
  };

  const pickExcelFile = () => excelFileInputRef.current?.click();

  const handleExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExcelError(""); setExcelBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
      if (!rows.length) { setExcelError("That file has no rows to import."); return; }
      const res = await api.post("/players/excel-parse", { rows });
      const parsedNames = (res.data.players || []).map(p => (p.name || "").trim()).filter(Boolean);
      if (!parsedNames.length) { setExcelError("Couldn't find any names in that spreadsheet."); return; }
      // Match against the existing roster by name -- Planner signs up
      // existing players, it never creates new ones (that's Players.jsx's
      // own Excel upload). Anything that doesn't match is flagged, not
      // silently dropped, so the organiser can see and fix it.
      const reviewRows = parsedNames.map(name => {
        const match = allPlayers.find(p => (p.name || "").trim().toLowerCase() === name.toLowerCase());
        return { name, matchedPlayer: match || null, include: !!match, buggy: false };
      });
      setExcelReview(reviewRows);
    } catch(e) {
      setExcelError("Failed to read file: " + (e.response?.data?.error || e.message));
    } finally { setExcelBusy(false); }
  };

  const confirmExcelSignups = async () => {
    const toAdd = (excelReview || []).filter(r => r.include && r.matchedPlayer);
    setConfirmingExcel(true);
    try {
      for (const r of toAdd) {
        try { await api.post(`/planner/${id}/respond`, { player_id: r.matchedPlayer.id, choice: "signup", buggy: r.buggy }); }
        catch(e) { console.error("Excel sign-up failed:", r.name, e.message); }
      }
      showToast(`Signed up ${toAdd.length} player(s)`);
      setExcelReview(null);
      load();
    } finally { setConfirmingExcel(false); }
  };

  // Same gap as the member-facing signup flows had (see the "+ Add Buggy"
  // fix there) -- the Players list itself was pure display, so a confirmed
  // player with no buggy had no way to get one added after the fact other
  // than re-running the whole Add Player flow. Reuses the same /respond
  // endpoint, just toggling buggy while keeping them signed up.
  const updateConfirmedPlayer = async (playerId, buggy) => {
    setUpdatingPlayerId(playerId);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: playerId, choice: "signup", buggy });
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't update that player"); }
    finally { setUpdatingPlayerId(null); }
  };

  // "Remove" = decline, same as a member's own "Can't make it" -- keeps
  // them visible in the Declined list below rather than erasing any
  // record they were ever confirmed, and automatically frees their place
  // for the reserve list exactly like a real cancellation would.
  const removePlayer = async (playerId, name) => {
    setUpdatingPlayerId(playerId);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: playerId, choice: "decline" });
      showToast(`${name} removed`);
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't remove that player"); }
    finally { setUpdatingPlayerId(null); }
  };

  // The reverse of removePlayer -- a declined player changing their mind
  // had no way back in before this: they're already "responded" so the
  // Select-a-player dropdown hides them, and the Declined list itself was
  // just static text with no action. Same /respond endpoint as everywhere
  // else, so capacity/reserve rules apply exactly as a fresh signup would
  // (goes to Reserve automatically if the fixture is already full).
  const resignUpPlayer = async (playerId, name) => {
    setUpdatingPlayerId(playerId);
    try {
      await api.post(`/planner/${id}/respond`, { player_id: playerId, choice: "signup" });
      showToast(`${name} signed up`);
      load();
    } catch(e) { showToast(e.response?.data?.error || "Couldn't sign that player up"); }
    finally { setUpdatingPlayerId(null); }
  };

  // Same decline mechanism as the per-row Remove button, just looped --
  // keeps every attendance record intact (shows up under Declined below),
  // nothing is deleted, and it automatically frees up reserve places same
  // as removing each one individually would.
  const removeAllPlayers = async () => {
    const everyone = [...confirmed, ...reserve];
    if (!everyone.length) return;
    if (!window.confirm(`Remove all ${everyone.length} player(s) (${confirmed.length} confirmed, ${reserve.length} reserve)? They'll show as Declined, same as using Remove on each one individually.`)) return;
    setRemovingAll(true);
    try {
      for (const p of everyone) {
        try { await api.post(`/planner/${id}/respond`, { player_id: p.player_id, choice: "decline" }); }
        catch(e) { console.error("Remove all: failed on", p.name, e.message); }
      }
      showToast("All players removed");
      load();
    } finally { setRemovingAll(false); }
  };

  const makeCurrent = async () => {
    setMakingCurrent(true);
    try {
      const res = await api.post(`/planner/${id}/make-current`);
      showToast(`Done — ${res.data.transferred} player(s) transferred to the live competition`);
      setConfirmMakeCurrent(false);
      load();
    } catch(e) {
      showToast(e.response?.data?.error || "Couldn't make this the current competition");
      setConfirmMakeCurrent(false);
      load(); // a 409 (already linked) means the UI was stale -- refresh so the "Go to Competition" button appears
    }
    finally { setMakingCurrent(false); }
  };

  const sendInvite = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await api.post(`/planner/${id}/invite`, { subject: inviteSubject, body: inviteBody, filter: inviteFilter });
      setSendResult(res.data);
      showToast(`Sent to ${res.data.sent.length} player(s)`);
    } catch(e) { showToast("Send failed"); }
    finally { setSending(false); }
  };

  const computedTeeTime = (i) => {
    const [h, m] = (fixture.first_tee_time || "09:00").split(":").map(Number);
    const total = h * 60 + m + i * (fixture.tee_interval_minutes || 8);
    const hh = Math.floor(total / 60) % 24, mm = total % 60;
    return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
  };
  const teeTimeFor = (i) => (fixture.tee_time_overrides && fixture.tee_time_overrides[i]) || computedTeeTime(i);
  const saveTeeTimeOverride = async (i, value) => {
    try { await api.put(`/planner/${id}/teetimes`, { overrides: { [i]: value } }); load(); }
    catch(e) { showToast("Couldn't save that tee time"); }
  };

  const deleteFixture = async () => {
    setDeletingFixture(true);
    try {
      await api.delete(`/planner/${id}`);
      navigate("/planner");
    } catch(e) { showToast(e.response?.data?.error || "Couldn't delete this fixture"); setDeletingFixture(false); }
  };
  const golfConfirmed = confirmed.filter(p => p.golf);
  const respondedIds = new Set([...confirmed, ...reserve, ...declined].map(p => p.player_id));
  // roster_member !== false excludes anyone only ever added via the
  // Dashboard's ad-hoc "Import Players from XL" flow -- Planner should only
  // ever offer real members. Mike: "in planner add player should be renamed
  // add member... this way we separate members and adhoc players."
  const addablePlayers = allPlayers.filter(p => !respondedIds.has(p.id) && p.roster_member !== false).sort((a,b) => (a.name||'').localeCompare(b.name||''));

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-3">
          <button onClick={() => navigate("/planner")} className="text-xs text-gray-500 hover:text-gray-700">← Planner</button>
          <span className="font-semibold text-sm">{fixture.event_name}</span>
          <span style={{background:st.bg,color:st.color,fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>{st.label.toUpperCase()}</span>
          {fixture.is_full && <span style={{background:"#fee2e2",color:"#991b1b",fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>FULL</span>}
          {reserve.length > 0 && <span style={{background:"#fef3c7",color:"#92400e",fontSize:"11px",fontWeight:700,padding:"4px 10px",borderRadius:"999px"}}>{reserve.length} RESERVE{reserve.length !== 1 ? "S" : ""}</span>}
          <div className="ml-auto flex items-center gap-2">
            {fixture.status === "draft" && (
              <button onClick={publish} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Publish</button>
            )}
            {fixture.status === "open" && (
              <button onClick={() => setConfirmMakeCurrent(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">▶ Make Current Competition</button>
            )}
            {fixture.status === "current" && (
              <button onClick={() => navigate("/players")} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">✅ Linked to Competition #{fixture.created_event_id} — Go to Competition →</button>
            )}
            {fixture.status !== "current" && fixture.status !== "completed" && (
              <button onClick={openEdit}
                style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",padding:"6px 12px",fontSize:"12px",fontWeight:700,color:"#1d4ed8",cursor:"pointer"}}>
                ✏️ Edit
              </button>
            )}
            <button onClick={() => setConfirmDeleteFixture(true)}
              style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"6px",padding:"6px 12px",fontSize:"12px",fontWeight:700,color:"#b91c1c",cursor:"pointer"}}>
              🗑️ Delete
            </button>
          </div>
        </div>

        <div style={{padding:"14px 20px",background:"white",borderBottom:"1px solid #e5e7eb",display:"flex",gap:"24px",fontSize:"13px",color:"#374151"}}>
          <div>📅 {new Date(fixture.event_date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
          <div>📍 {fixture.venue || "no venue set"}</div>
          <div>⛳ {golfConfirmed.length} confirmed{placesLeft != null && ` · ${placesLeft} places left`}</div>
          {fixture.closing_date && (
            <div>{fixture.effective_status === "closed" ? "Closed" : "Closes"} {new Date(fixture.closing_date).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
          )}
        </div>

        <div style={{display:"flex",gap:"4px",background:"white",borderBottom:"1px solid #e5e7eb",padding:"0 16px"}}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{padding:"10px 14px",fontSize:"13px",fontWeight:tab===t?700:500,color:tab===t?"#14532d":"#6b7280",borderBottom:tab===t?"2px solid #14532d":"2px solid transparent",background:"none",border:"none",borderBottomWidth:"2px",cursor:"pointer"}}>
              {t}{t==="Reserves" && reserve.length>0 ? ` (${reserve.length})` : ""}
            </button>
          ))}
        </div>

        <div style={{padding:"20px",overflow:"auto",flex:1,background:"#f9fafb"}}>
          {tab === "Players" && (
            <div>
              <div style={{marginBottom:"14px"}}>
                {!showAddPlayer ? (
                  <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                    <button onClick={() => setShowAddPlayer(true)} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">+ Add Member</button>
                    <button onClick={pickExcelFile} disabled={excelBusy}
                      style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",color:"#1d4ed8",fontSize:"12px",fontWeight:700,cursor:"pointer",padding:"6px 12px",opacity:excelBusy?0.6:1}}>
                      {excelBusy ? "Reading…" : "📊 Upload Excel"}
                    </button>
                    {(confirmed.length > 0 || reserve.length > 0) && (
                      <button onClick={removeAllPlayers} disabled={removingAll}
                        style={{background:"none",border:"1px solid #fecaca",borderRadius:"6px",color:"#b91c1c",fontSize:"12px",fontWeight:700,cursor:"pointer",padding:"6px 12px",opacity:removingAll?0.6:1}}>
                        {removingAll ? "Removing…" : "✕ Remove All"}
                      </button>
                    )}
                    <input ref={excelFileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{display:"none"}} onChange={handleExcelFile} />
                    {excelError && <span style={{color:"#b91c1c",fontSize:"12px"}}>{excelError}</span>}
                  </div>
                ) : (
                  <div style={{background:"white",borderRadius:"8px",padding:"14px",border:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap"}}>
                    <select value={addPlayerId} onChange={e => setAddPlayerId(e.target.value)}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"6px 10px",fontSize:13,minWidth:"200px"}}>
                      <option value="">Select a member…</option>
                      {addablePlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("signup", false)}
                      style={{background:"#166534",color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Sign Up
                    </button>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("signup", true)}
                      style={{background:"#1d4ed8",color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Buggy Required
                    </button>
                    <button disabled={!addPlayerId || addingPlayer} onClick={() => addPlayerManually("decline", false)}
                      style={{background:"none",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer",opacity:(!addPlayerId||addingPlayer)?0.5:1}}>
                      Can't make it
                    </button>
                    <button onClick={() => { setShowAddPlayer(false); setAddPlayerId(""); }}
                      style={{background:"none",color:"#9ca3af",border:"none",fontSize:12,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {confirmed.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No confirmed players yet.</div>}
              {confirmed.map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:"white",borderRadius:"8px",marginBottom:"6px",border:"1px solid #e5e7eb"}}>
                  <div style={{flex:1,fontWeight:600,fontSize:"13px"}}>{p.name}</div>
                  <div style={{fontSize:"12px",color:"#6b7280",display:"flex",alignItems:"center",gap:"10px"}}>
                    {p.golf && <span>⛳ Signed Up</span>}
                    {p.buggy ? (
                      <span>🚗 Buggy</span>
                    ) : p.golf && (
                      <button onClick={() => updateConfirmedPlayer(p.player_id, true)} disabled={updatingPlayerId === p.player_id}
                        style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",color:"#1d4ed8",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"3px 8px"}}>
                        + Add Buggy
                      </button>
                    )}
                    <button onClick={() => removePlayer(p.player_id, p.name)} disabled={updatingPlayerId === p.player_id}
                      style={{background:"none",border:"1px solid #fecaca",borderRadius:"6px",color:"#b91c1c",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"3px 8px"}}>
                      ✕ Remove
                    </button>
                  </div>
                </div>
              ))}
              {declined.length > 0 && (
                <>
                  <div style={{fontSize:"12px",fontWeight:700,color:"#9ca3af",margin:"18px 0 6px"}}>Declined</div>
                  {declined.map(p => (
                    <div key={p.id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"8px 14px",fontSize:"13px",color:"#9ca3af"}}>
                      <div style={{flex:1}}>{p.name}</div>
                      <button onClick={() => resignUpPlayer(p.player_id, p.name)} disabled={updatingPlayerId === p.player_id}
                        style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:"6px",color:"#1d4ed8",fontSize:"11px",fontWeight:700,cursor:"pointer",padding:"3px 8px"}}>
                        ↩ Sign Up
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {tab === "Reserves" && (
            <div>
              {reserve.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No one on the reserve list.</div>}
              {reserve.map(p => (
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:"12px",padding:"10px 14px",background:"white",borderRadius:"8px",marginBottom:"6px",border:"1px solid #fde68a"}}>
                  <div style={{fontSize:"12px",fontWeight:700,color:"#92400e",width:"24px"}}>#{p.reserve_position}</div>
                  <div style={{flex:1,fontWeight:600,fontSize:"13px"}}>{p.name}</div>
                  <button onClick={() => promote(p.id)} className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Promote to Confirmed</button>
                </div>
              ))}
            </div>
          )}

          {tab === "Costs" && (() => {
            const rows = [["Golf", fixture.golf_cost, p=>p.golf],["Dinner", fixture.dinner_cost, p=>p.dinner],["Buggy", fixture.buggy_cost, p=>p.buggy]]
              .map(([label, cost, pred]) => {
                const count = confirmed.filter(pred).length;
                const total = cost != null ? count * parseFloat(cost) : null;
                return { label, cost, count, total };
              });
            if (fixture.other_cost != null) rows.push({ label: "Other", cost: fixture.other_cost, count: confirmed.length, total: confirmed.length * parseFloat(fixture.other_cost) });
            const grandTotal = rows.reduce((sum, r) => sum + (r.total || 0), 0);
            return (
              <div style={{background:"white",borderRadius:"10px",padding:"20px",border:"1px solid #e5e7eb",maxWidth:"480px"}}>
                <div style={{fontSize:"13px",fontWeight:700,color:"#374151",marginBottom:"10px"}}>Expected Cost</div>
                {rows.map(r => (
                  <div key={r.label} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f3f4f6",fontSize:"13px"}}>
                    <span>{r.label}: {r.count} × {r.cost != null ? `£${r.cost}` : "—"}</span>
                    <span style={{fontWeight:700}}>{r.total != null ? `£${r.total.toFixed(2)}` : "—"}</span>
                  </div>
                ))}
                <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 0",fontSize:"14px",fontWeight:800,color:"#14532d"}}>
                  <span>Expected total</span><span>£{grandTotal.toFixed(2)}</span>
                </div>
                <div style={{fontSize:"11px",color:"#9ca3af",marginTop:"12px"}}>Expected, not Amount Due or Paid — no payment collection yet (planned for a later release).</div>
              </div>
            );
          })()}

          {tab === "Tee Times" && (
            <div style={{background:"white",borderRadius:"10px",padding:"20px",border:"1px solid #e5e7eb",maxWidth:"360px"}}>
              <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"12px"}}>First tee {fixture.first_tee_time}, every {fixture.tee_interval_minutes} mins — click a time to adjust it if the club's given you an odd gap. Informational until this fixture is made current.</div>
              {golfConfirmed.length === 0 && <div style={{color:"#9ca3af",fontSize:"13px"}}>No golf signups yet.</div>}
              {Array.from({length: Math.ceil(golfConfirmed.length / 4)}).map((_, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f3f4f6",fontSize:"13px"}}>
                  <input type="time" value={teeTimeFor(i)} onChange={e => saveTeeTimeOverride(i, e.target.value)}
                    style={{fontWeight:700,border:"1px solid transparent",borderRadius:6,padding:"2px 4px",fontSize:13,fontFamily:"inherit"}}
                    onFocus={e => e.target.style.border="1px solid #d1d5db"} onBlur={e => e.target.style.border="1px solid transparent"} />
                  <span style={{color:"#6b7280"}}>{golfConfirmed.slice(i*4,i*4+4).map(p=>p.name).join(", ")}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "Communications" && (
            <div style={{background:"white",borderRadius:"10px",padding:"20px",border:"1px solid #e5e7eb",maxWidth:"640px"}}>
              <div style={{display:"flex",gap:"8px",marginBottom:"16px",flexWrap:"wrap"}}>
                {[["all","Invite Members"],["non_responders","Email Non-Responders"],["confirmed","Email Confirmed Players"],["reserve","Email Reserve List"]].map(([val,label]) => (
                  <button key={val} onClick={() => setInviteFilter(val)}
                    style={{padding:"6px 12px",fontSize:"12px",fontWeight:700,borderRadius:"999px",border:inviteFilter===val?"1px solid #166534":"1px solid #d1d5db",background:inviteFilter===val?"#dcfce7":"white",color:inviteFilter===val?"#166534":"#6b7280",cursor:"pointer"}}>
                    {label}
                  </button>
                ))}
              </div>
              <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Subject</label>
              <input value={inviteSubject} onChange={e => setInviteSubject(e.target.value)}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",marginBottom:"12px"}} />
              <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Body</label>
              <textarea value={inviteBody} onChange={e => setInviteBody(e.target.value)} rows={10}
                style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box",fontFamily:"inherit"}} />
              <div style={{fontSize:12,color:"#6b7280",margin:"8px 0 16px"}}>
                Merge tags: {MERGE_TAGS.map(t => <code key={t} style={{background:"#eef2ff",color:"#3730a3",borderRadius:4,padding:"1px 5px",marginRight:6,fontSize:12}}>{`{{${t}}}`}</code>)}
              </div>
              <button onClick={sendInvite} disabled={sending}
                style={{background:"#6b21a8",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:sending?0.5:1}}>
                {sending ? "Sending…" : "Send"}
              </button>
              {sendResult && (
                <div style={{marginTop:"12px",fontSize:"12px",color:"#374151"}}>
                  Sent to {sendResult.sent.length}. {sendResult.skipped.length > 0 && <>Skipped (no email / failed): {sendResult.skipped.join(", ")}</>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showEditForm && editForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"640px",width:"100%",maxHeight:"90vh",overflow:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#14532d",marginBottom:"16px"}}>✏️ Edit Fixture</div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              <div style={{gridColumn:"1 / -1"}}>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Event name</label>
                <input value={editForm.event_name} onChange={e => setEditForm(f => ({...f, event_name: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Date</label>
                <input type="date" value={editForm.event_date} onChange={e => setEditForm(f => ({...f, event_date: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Venue</label>
                <input value={editForm.venue} onChange={e => setEditForm(f => ({...f, venue: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Course</label>
                <select value={editForm.course_id} onChange={e => setEditForm(f => ({...f, course_id: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}}>
                  <option value="">— none —</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Format</label>
                <select value={editForm.game_type} onChange={e => setEditForm(f => ({...f, game_type: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}}>
                  {GAME_TYPES.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>HCP cap (optional)</label>
                <input type="number" value={editForm.hcp_cap} onChange={e => setEditForm(f => ({...f, hcp_cap: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>First tee time</label>
                <input type="time" value={editForm.first_tee_time} onChange={e => setEditForm(f => ({...f, first_tee_time: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Tee interval (mins)</label>
                <input type="number" value={editForm.tee_interval_minutes} onChange={e => setEditForm(f => ({...f, tee_interval_minutes: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Max players (golf)</label>
                <input type="number" value={editForm.max_players} onChange={e => setEditForm(f => ({...f, max_players: e.target.value}))}
                  placeholder="No cap" style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div>
                <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>Closing date</label>
                <input type="date" value={editForm.closing_date} onChange={e => setEditForm(f => ({...f, closing_date: e.target.value}))}
                  style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
              </div>
              <div style={{gridColumn:"1 / -1",borderTop:"1px solid #e5e7eb",paddingTop:"12px",display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:"12px"}}>
                {[["golf_cost","Golf £"],["dinner_cost","Dinner £"],["buggy_cost","Buggy £"],["other_cost","Other £"]].map(([key, label]) => (
                  <div key={key}>
                    <label style={{display:"block",fontSize:12,fontWeight:700,color:"#374151",marginBottom:4}}>{label}</label>
                    <input type="number" step="0.01" value={editForm[key]} onChange={e => setEditForm(f => ({...f, [key]: e.target.value}))}
                      style={{width:"100%",border:"1px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:14,boxSizing:"border-box"}} />
                  </div>
                ))}
              </div>
            </div>

            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"20px"}}>
              <button onClick={() => setShowEditForm(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={saveEdit} disabled={savingEdit}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:savingEdit?0.5:1}}>
                {savingEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmMakeCurrent && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"440px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#1e40af",marginBottom:"8px"}}>▶ Make "{fixture.event_name}" the Current Competition?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This creates a real competition from this fixture and ticks every confirmed golfer as Playing on the Players screen ({golfConfirmed.length} player(s)). Dinner-only signups won't be ticked. Any other active competition will be deactivated. This can't be undone from here — you'd need to edit the new competition by hand afterward.
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmMakeCurrent(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={makeCurrent} disabled={makingCurrent}
                style={{background:"#1e40af",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:makingCurrent?0.5:1}}>
                {makingCurrent ? "Working…" : "Yes, Make Current"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteFixture && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"400px",width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#b91c1c",marginBottom:"8px"}}>⚠️ Delete "{fixture.event_name}"?</div>
            <div style={{fontSize:"13px",color:"#374151",marginBottom:"16px"}}>
              This removes the fixture and every signup against it ({confirmed.length + reserve.length + declined.length} response(s)). This can't be undone.
              {fixture.status === "current" && (
                <><br/><br/><strong>Note:</strong> this fixture is linked to Competition #{fixture.created_event_id}. Deleting it here only removes the Planner record — the real competition, its Players/Groups/Scores, are not affected and must be deleted separately from Players if you want those gone too.</>
              )}
            </div>
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end"}}>
              <button onClick={() => setConfirmDeleteFixture(false)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={deleteFixture} disabled={deletingFixture}
                style={{background:"#b91c1c",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:deletingFixture?0.5:1}}>
                {deletingFixture ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {excelReview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
          <div style={{background:"white",borderRadius:"12px",padding:"24px",maxWidth:"520px",width:"100%",maxHeight:"80vh",overflow:"auto",boxShadow:"0 8px 32px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:"16px",fontWeight:"700",color:"#166534",marginBottom:"4px"}}>Confirm sign-ups from spreadsheet</div>
            <div style={{fontSize:"12px",color:"#6b7280",marginBottom:"10px"}}>
              Matched against the existing player roster — Planner can only sign up players who already exist in Players, not create new ones.
            </div>
            {excelReview.some(r => r.matchedPlayer) && (
              <div style={{display:"flex",gap:"8px",marginBottom:"10px"}}>
                <button onClick={() => setExcelReview(prev => prev.map(row => row.matchedPlayer ? {...row, include: true} : row))}
                  style={{background:"#f0fdf4",border:"1px solid #16a34a",color:"#166534",borderRadius:"6px",padding:"4px 10px",fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>
                  Select All
                </button>
                <button onClick={() => setExcelReview(prev => prev.map(row => row.matchedPlayer ? {...row, include: false} : row))}
                  style={{background:"none",border:"1px solid #d1d5db",color:"#6b7280",borderRadius:"6px",padding:"4px 10px",fontSize:"11px",fontWeight:"600",cursor:"pointer"}}>
                  Deselect All
                </button>
              </div>
            )}
            {excelReview.filter(r => r.matchedPlayer).map((r, i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:"10px",padding:"6px 0",borderBottom:"1px solid #f3f4f6"}}>
                <input type="checkbox" checked={r.include} onChange={e => setExcelReview(prev => prev.map(row => row === r ? {...row, include: e.target.checked} : row))} />
                <div style={{flex:1,fontSize:"13px"}}>{r.matchedPlayer.name}</div>
                <label style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"11px",color:"#6b7280",cursor:"pointer"}}>
                  <input type="checkbox" checked={r.buggy} onChange={e => setExcelReview(prev => prev.map(row => row === r ? {...row, buggy: e.target.checked} : row))} />
                  Buggy
                </label>
              </div>
            ))}
            {excelReview.some(r => !r.matchedPlayer) && (
              <>
                <div style={{fontSize:"12px",fontWeight:700,color:"#b91c1c",margin:"14px 0 6px"}}>Not found in the player roster — add to Players first, then re-upload:</div>
                {excelReview.filter(r => !r.matchedPlayer).map((r, i) => (
                  <div key={i} style={{fontSize:"13px",color:"#991b1b",padding:"2px 0"}}>{r.name}</div>
                ))}
              </>
            )}
            <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"18px"}}>
              <button onClick={() => setExcelReview(null)}
                style={{background:"none",border:"1px solid #d1d5db",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",cursor:"pointer",color:"#6b7280"}}>
                Cancel
              </button>
              <button onClick={confirmExcelSignups} disabled={confirmingExcel || !excelReview.some(r => r.include && r.matchedPlayer)}
                style={{background:"#166534",color:"white",border:"none",borderRadius:"8px",padding:"8px 20px",fontSize:"13px",fontWeight:"700",cursor:"pointer",opacity:(confirmingExcel || !excelReview.some(r => r.include && r.matchedPlayer))?0.5:1}}>
                {confirmingExcel ? "Signing up…" : `Sign Up ${excelReview.filter(r => r.include && r.matchedPlayer).length} Player(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
